import { ok } from '@oazapfts/runtime';
import * as rq from '@tanstack/react-query';
import { z } from 'zod';

import { FeApi, useInjectFeApiContext } from '@/api/fe-api';
import {
  SERVER_ERROR_MESSAGE,
  SYSTEM_DEPENDENCY_FAILED_ERROR_MESSAGE,
} from '@/system-logs/constants/error-messages';
import { Table } from '@/system-logs/entities/system-logs';
import { getQueryParamsFromFilters } from '@/system-logs/utils/get-query-params-from-filters';
import type { Filters } from '@/system-logs/view/state/selected-filters';

export namespace ListSystemLogs {
  export const key = (input: Input) => [
    'system-logs',
    'list',
    ...Object.entries(input).flatMap(([key, value]) => [key, value]),
  ];

  export type Input = {
    filters: Filters;
    page: number;
    pageSize: number;
    systemId?: string;
  };

  export const OutputSchema = z.object({
    totalCount: z.number(),
    table: Table.Schema,
    next: z.string().optional(),
    previous: z.string().optional(),
  });
  export type Output = z.infer<typeof OutputSchema>;

  export type QueryParams = {
    accureLevel?:
      | 'array'
      | 'bank'
      | 'cell'
      | 'container'
      | 'module'
      | 'node'
      | 'pack'
      | 'pcs'
      | 'system';
    domain?: 'battery' | 'pcs' | 'other';
    endDate?: string;
    limit: number;
    offset?: number;
    siteAccureId?: string;
    startDate?: string;
    unitAccureId?: string;
  };

  export function useQuery({ filters, page, pageSize, systemId }: Input) {
    const systemLogsList = useInjectFeApiContext(
      FeApi.systemLogsV1LogsListRetrieve,
    );

    return rq.useQuery({
      queryKey: key({ filters, page, pageSize, systemId }),
      queryFn: async (): Promise<Output> => {
        const offset = (page - 1) * pageSize;

        const queryParams: QueryParams = {
          limit: pageSize,
          ...getQueryParamsFromFilters({ systemId, filters }),
        };

        // Offset is optional in OpenAPI, but always sent here for paging
        if (offset > 0) {
          queryParams.offset = offset;
        }

        // All required/optional params are now mapped
        try {
          const resp = await ok(systemLogsList(queryParams));
          return {
            totalCount: resp.count,
            table: resp.results,
            next: resp.next ?? undefined,
            previous: resp.previous ?? undefined,
          };
        } catch (error: any) {
          // Enhanced error handling for specific cases
          if (error?.status === 424) {
            throw new Error(
              `${SYSTEM_DEPENDENCY_FAILED_ERROR_MESSAGE}: ${JSON.stringify(error.data)}`,
            );
          }
          if (error?.status === 500) {
            throw new Error(
              `${SERVER_ERROR_MESSAGE}: ${JSON.stringify(error.data)}`,
            );
          }
          // Re-throw other errors as-is
          throw error;
        }
      },
      /**
       * We do no frontend caching for this query because it is already done in the backend.
       */
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });
  }
}
